import { formatNumber } from "$root/localization/formatters";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { ICellRendererParams } from "ag-grid-community";
import { PortfolioLabContext } from "../../../ctx";
import type { MergedCompositionRow } from "../rows";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { labWeightTotalDisplay } from "../../../common";
import { useDecimalPlaces } from "$root/functional-areas/user/useDecimalPlaces";
import { type AutoTooltipProps, AutoTooltip } from "@mdotm/shadcn/legacy/components";
import { useCallback } from "react";

type LabTotalsWeightCellRendererProps = ICellRendererParams<MergedCompositionRow> & {
	uuid: string;
};

/**
 * Self-subscribing totals cell: the total is summed from `investments$` rather than read off the
 * pinned row's `totalWeight`. Every column value on the totals row is null, so AG Grid's
 * value-equality refresh skips these cells and their `data` prop keeps the composition as it was
 * when the row first rendered.
 */
export function LabTotalsWeightCellRenderer({ uuid }: LabTotalsWeightCellRendererProps) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const investment = useAtomValue(ctx.investments$).find((i) => i.virtualUuid === uuid);
	const { customerDecimalPlacesPreferences } = useDecimalPlaces();
	if (!investment) {
		return <div />;
	}
	return (
		<LabTotalsWeight {...labWeightTotalDisplay(investment.currentComposition, customerDecimalPlacesPreferences)} />
	);
}

function LabTotalsWeight({ valid, text, total }: ReturnType<typeof labWeightTotalDisplay>) {
	const trigger = useCallback<AutoTooltipProps<HTMLSpanElement>["trigger"]>(
		({ innerRef }) => (
			<span
				ref={innerRef}
				data-qualifier="PortfolioLab/Totals/Weight"
				data-valid={String(valid)}
				className={toClassName({
					"font-medium": true,
					"text-[color:var(--palette-D500)]": !valid,
				})}
			>
				{text}
			</span>
		),
		[valid, text],
	);
	return (
		<div className="flex flex-col justify-center leading-tight h-full">
			<AutoTooltip trigger={trigger} hideArrow>
				{`${formatNumber(total, { maxDecimalPlaces: 16, minDecimalPlaces: 0 })}%`}
			</AutoTooltip>
		</div>
	);
}
